import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval, format, isSameMonth, isSameDay, isToday } from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Ticket, CalendarCheck, MapPin, Users, Sparkles, X } from "lucide-react";
import { tourTitle, parseTourDates, isFutureRequest } from "@/lib/fields";
import { api } from "@/lib/api";
import { FutureRequestDialog } from "@/components/FutureRequestDialog";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const compactDates = (val) => {
  const arr = parseTourDates(val);
  if (!arr.length) return "No date yet";
  const sorted = [...arr].sort((a, b) => a - b);
  const first = format(sorted[0], "EEE, MMM d, yyyy");
  return sorted.length > 1 ? `${first}  +${sorted.length - 1} more` : first;
};

// Dedicated page for tentative "Future Request" tours: a mini month calendar + a list,
// each linked to the guest page with a one-tap "Convert to tour".
export const FutureRequests = ({ guests = [], onConvert, onChanged }) => {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [tipDismissed, setTipDismissed] = useState(() => localStorage.getItem("sd_fr_tip") === "1");
  const dismissTip = () => { localStorage.setItem("sd_fr_tip", "1"); setTipDismissed(true); };

  const requests = useMemo(() => guests.filter(isFutureRequest), [guests]);

  const byDay = useMemo(() => {
    const m = {};
    requests.forEach((g) => {
      parseTourDates(g.tour_date).forEach((d) => {
        const k = format(d, "yyyy-MM-dd");
        (m[k] = m[k] || []).push(g);
      });
    });
    return m;
  }, [requests]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor));
    const end = endOfWeek(endOfMonth(cursor));
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const list = useMemo(() => {
    if (selected) return byDay[format(selected, "yyyy-MM-dd")] || [];
    const now = Date.now();
    return [...requests].sort((a, b) => {
      const da = parseTourDates(a.tour_date), db = parseTourDates(b.tour_date);
      const na = da.length ? Math.min(...da.map((x) => Math.abs(x.getTime() - now))) : Infinity;
      const nb = db.length ? Math.min(...db.map((x) => Math.abs(x.getTime() - now))) : Infinity;
      if (na !== nb) return na - nb;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [selected, byDay, requests]);

  return (
    <div data-testid="future-requests-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-800 tracking-tight text-zinc-50">
          Future Requests
          <span className="ml-2 text-sm font-400 text-zinc-600">({requests.length})</span>
        </h2>
        <button onClick={() => setAddOpen(true)} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#9B2226]/50 bg-[#9B2226]/10 px-3 text-sm font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20" data-testid="fr-add-btn">
          <Ticket className="h-4 w-4" /> Add request
        </button>
      </div>

      {!tipDismissed && (
      <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-indigo-500/30 bg-indigo-500/10 p-4 pr-9" data-testid="fr-tip">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-300"><Sparkles className="h-5 w-5" /></div>
        <p className="min-w-0 text-xs text-zinc-300">
          <span className="font-800 text-zinc-50">Future Requests</span> are tentative tours — a guest wants to tour with you and hasn't yet booked or been scheduled. They show on your calendar and Device Calendar as all-day placeholders and don't count in your stats. When a tour is confirmed, tap <span className="text-[#E0575B]">Convert to tour</span>.
        </p>
        <button onClick={dismissTip} aria-label="Dismiss" data-testid="fr-tip-dismiss" className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-500/10 hover:text-zinc-300">
          <X className="h-4 w-4" />
        </button>
      </div>
      )}

      {/* Mini month calendar */}
      <div className="mb-5 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3" data-testid="fr-mini-calendar">
        <div className="mb-2 flex items-center justify-between">
          <button onClick={() => setCursor((c) => subMonths(c, 1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" data-testid="fr-prev-month" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-700 text-zinc-100">{format(cursor, "MMMM yyyy")}</span>
            <button onClick={() => { setCursor(new Date()); setSelected(null); }} className="rounded-md px-2 py-0.5 text-[11px] font-600 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="fr-today-btn">Today</button>
          </div>
          <button onClick={() => setCursor((c) => addMonths(c, 1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" data-testid="fr-next-month" aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wide text-zinc-600">
          {WEEKDAYS.map((d, i) => <div key={i} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const k = format(d, "yyyy-MM-dd");
            const has = !!byDay[k];
            const inMonth = isSameMonth(d, cursor);
            const isSel = selected && isSameDay(d, selected);
            return (
              <button
                key={k}
                onClick={() => setSelected(isSel ? null : d)}
                data-testid={`fr-day-${k}`}
                className={`relative flex aspect-square items-center justify-center rounded-lg text-sm transition-colors ${isSel ? "bg-indigo-500/30 text-indigo-100 ring-1 ring-indigo-400" : has ? "bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20" : inMonth ? "text-zinc-300 hover:bg-zinc-800" : "text-zinc-700"} ${isToday(d) && !isSel ? "ring-1 ring-zinc-600" : ""}`}
              >
                {format(d, "d")}
                {has && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-indigo-400" />}
              </button>
            );
          })}
        </div>
        {selected && (
          <button onClick={() => setSelected(null)} className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200" data-testid="fr-clear-day">
            <X className="h-3 w-3" /> Showing {format(selected, "MMM d")} — clear filter
          </button>
        )}
      </div>

      {/* List */}
      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 py-12 text-center" data-testid="fr-empty">
          <CalendarDays className="mx-auto mb-2 h-8 w-8 text-zinc-700" />
          <p className="text-sm text-zinc-400">{selected ? "No requests on this day." : "No future requests yet."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((g) => (
            <div key={g.id} className="flex items-center gap-2 rounded-xl border border-indigo-500/25 bg-indigo-500/[0.06] p-3" data-testid={`fr-item-${g.id}`}>
              <button onClick={() => setEditReq(g)} className="flex min-w-0 flex-1 flex-col items-start text-left" data-testid={`fr-open-${g.id}`}>
                <span className="flex w-full items-center gap-2">
                  <Ticket className="h-4 w-4 shrink-0 text-indigo-300" />
                  <span className="truncate font-600 text-zinc-100">{tourTitle(g)}</span>
                  <span className="shrink-0 rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-600 uppercase tracking-wide text-indigo-300">Request</span>
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-400">
                  <span className="inline-flex items-center gap-1 text-indigo-300/90"><CalendarDays className="h-3 w-3" /> {compactDates(g.tour_date)}</span>
                  {(g.resort_meet) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {g.resort_meet}</span>}
                  {g.party_size ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {g.party_size}</span> : null}
                </span>
              </button>
              {onConvert && (
                <button
                  onClick={(e) => { e.stopPropagation(); onConvert(g); }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#9B2226]/50 bg-[#9B2226]/10 px-2.5 py-1.5 text-[11px] font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20"
                  data-testid={`fr-convert-${g.id}`}
                  title="Confirm a date and move this into your real schedule"
                >
                  <CalendarCheck className="h-3.5 w-3.5" /> Convert to tour
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <FutureRequestDialog open={addOpen} onOpenChange={setAddOpen} onCreated={() => onChanged?.()} />
      <FutureRequestDialog
        open={!!editReq}
        request={editReq}
        onOpenChange={(o) => !o && setEditReq(null)}
        onCreated={() => onChanged?.()}
        onConvert={onConvert}
        onDelete={async (g) => { try { await api.deleteGuest(g.id); toast.success("Request deleted"); onChanged?.(); } catch { toast.error("Couldn't delete"); } }}
      />
    </div>
  );
};
